import { createApi } from "@reduxjs/toolkit/query/react";
import { FilterValue } from "interfaces/FilterTypes";
import {
  AutomationList,
  FeeCollectionList,
  InvoiceList,
  MandateList,
  ProformaList,
} from "interfaces/Revenue";
import { customBaseQuery, handleQueryLifecycle } from "store/utils";
import { feeCalculationApi } from "./FeeCalculationApi";

type Id = string | number;

export interface ProformaListResponse {
  data: ProformaList[];
  totalCount: number;
}
export interface InvoiceListResponse {
  data: InvoiceList[];
  totalCount: number;
}
export interface FeeCollectionListResponse {
  data: FeeCollectionList[];
  totalCount: number;
}
export interface MandateListResponse {
  data: MandateList[];
  totalCount: number;
}
export interface AutomationListResponse {
  data: AutomationList[];
  totalCount: number;
}

export const proformaApi = createApi({
  reducerPath: "proformaApi",
  baseQuery: customBaseQuery,
  tagTypes: [
    "ProformaList",
    "ProformaItem",
    "InvoiceList",
    "InvoiceItem",
    "FeeCollectionList",
    "FeeCollectionItem",
    "MandateList",
    "MandateItem",
    "AutomationList",
    "AutomationItem",
  ],
  endpoints: (builder) => ({
    // PROFORMA APIs
    getProformaList: builder.query<ProformaListResponse, FilterValue>({
      query: (params) => ({
        url: "/api/revenue/proforma-history/",
        params,
      }),
      providesTags: (result) => {
        return result?.data
          ? [
              ...result.data?.map((item: ProformaList) => ({
                type: "ProformaItem" as const,
                id: item.id,
              })),
              { type: "ProformaList" as const, id: "LIST" },
            ]
          : [{ type: "ProformaList" as const, id: "LIST" }];
      },
    }),

    deleteProforma: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/proforma-history/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "ProformaList", id: "LIST" }] : [],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await handleQueryLifecycle(queryFulfilled);
        dispatch(feeCalculationApi.util.invalidateTags(["FeeCalculations"]));
      },
    }),

    proformaConvertToInvoice: builder.mutation<any, any>({
      query: ({ data, id }) => ({
        url: `/api/revenue/proforma-history/${id}/convert-invoice/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "ProformaList", id: "LIST" }] : [],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await handleQueryLifecycle(queryFulfilled);
        dispatch(proformaApi.util.invalidateTags(["InvoiceList"]));
      },
    }),

    sendProforma: builder.mutation<any, { id: string; type: string }>({
      query: ({ id, type }) => {
        if (type === "SEND_PROFORMA_ONLY") {
          return {
            url: `/api/revenue/proforma-history/${id}/send-proforma/`,
            method: "POST",
          };
        }
        return {
          url: `/api/revenue/proforma-history/${id}/send-proforma-with-payment-link/`,
          method: "POST",
        };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "ProformaList", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) => {
        await handleQueryLifecycle(queryFulfilled);
      },
    }),

    // INVOICE APIs
    getInvoiceList: builder.query<InvoiceListResponse, FilterValue>({
      query: (params) => ({
        url: "/api/revenue/invoice-history/",
        params,
      }),
      providesTags: (result) => {
        return result?.data
          ? [
              ...result.data?.map((item: InvoiceList) => ({
                type: "InvoiceItem" as const,
                id: item.id,
              })),
              { type: "InvoiceList" as const, id: "LIST" },
            ]
          : [{ type: "InvoiceList" as const, id: "LIST" }];
      },
    }),

    deleteInvoice: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/invoice-history/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "InvoiceList", id: "LIST" }] : [],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await handleQueryLifecycle(queryFulfilled);
        dispatch(proformaApi.util.invalidateTags(["ProformaList"]));
      },
    }),

    // FEE COLLECTION APIs
    getFeeCollectionList: builder.query<FeeCollectionListResponse, FilterValue>(
      {
        query: (params) => ({
          url: "/api/revenue/fee-collection-history/",
          params,
        }),
        providesTags: (result) => {
          return result?.data
            ? [
                ...result.data?.map((item: FeeCollectionList) => ({
                  type: "FeeCollectionItem" as const,
                  id: item.id,
                })),
                { type: "FeeCollectionList" as const, id: "LIST" },
              ]
            : [{ type: "FeeCollectionList" as const, id: "LIST" }];
        },
      },
    ),

    deleteFeeCollection: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/fee-collection-history/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "FeeCollectionList", id: "LIST" }] : [],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    fetchClientInvoices: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/invoice-history/unpaid/?client_id=${id}`,
        method: "GET",
      }),
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    createCollection: builder.mutation<any, any>({
      query: (data) => ({
        url: "/api/revenue/fee-collection-history/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "FeeCollectionList", id: "LIST" }] : [],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    updateCollection: builder.mutation<any, { data: any; id: Id }>({
      query: ({ data, id }) => ({
        url: `/api/revenue/fee-collection-history/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "FeeCollectionList", id: "LIST" }] : [],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    fetchCollectionInvoicesById: builder.query<any, string | number>({
      query: (id) => ({ url: `/api/revenue/fee-collection-history/${id}/` }),
      keepUnusedDataFor: 0,
    }),

    // MANDATE APIs
    getMandateList: builder.query<MandateListResponse, FilterValue>({
      query: (params) => ({
        url: "/api/revenue/mandate/",
        params,
      }),
      providesTags: (result) => {
        return result?.data
          ? [
              ...result.data?.map((item: MandateList) => ({
                type: "MandateItem" as const,
                id: item.id,
              })),
              { type: "MandateList" as const, id: "LIST" },
            ]
          : [{ type: "MandateList" as const, id: "LIST" }];
      },
    }),

    fetchMandateById: builder.query<any, Id>({
      query: (id) => ({ url: `/api/revenue/mandate/${id}/` }),
    }),

    createMandate: builder.mutation<any, any>({
      query: (data) => ({
        url: "/api/revenue/mandate/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "MandateList", id: "LIST" }] : [],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    cancelMandate: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/mandate/${id}/cancel/`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "MandateList", id: "LIST" }] : [],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    // AUTOMATION APIs
    getAutomationList: builder.query<AutomationListResponse, FilterValue>({
      query: (params) => ({
        url: "/api/revenue/auto-invoice-flow/",
        params,
      }),
      providesTags: (result) => {
        return result?.data
          ? [
              ...result.data?.map((item: AutomationList) => ({
                type: "AutomationItem" as const,
                id: item.id,
              })),
              { type: "AutomationList" as const, id: "LIST" },
            ]
          : [{ type: "AutomationList" as const, id: "LIST" }];
      },
    }),

    deleteSingleAutomation: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/auto-invoice-flow/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "AutomationList", id: "LIST" }],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    bulkDeleteAutomation: builder.mutation<any, { ids: Id[] }>({
      query: ({ ids }) => ({
        url: "/api/revenue/auto-invoice-flow/bulk-delete/",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "AutomationList", id: "LIST" }] : [],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    createSingleAutomation: builder.mutation<any, any>({
      query: (data) => ({
        url: "/api/revenue/auto-invoice-flow/bulk-create/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "AutomationList", id: "LIST" }] : [],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    updateSingleAutomation: builder.mutation<any, any>({
      query: (data) => ({
        url: "/api/revenue/auto-invoice-flow/bulk-update/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "AutomationList", id: "LIST" }] : [],
      onQueryStarted: (_, { queryFulfilled }) => {
        handleQueryLifecycle(queryFulfilled);
      },
    }),

    bulkAutomation: builder.mutation<any, { data: any; isUpdate: boolean }>({
      query: ({ data, isUpdate }) => {
        if (isUpdate) {
          return {
            url: "api/revenue/auto-invoice-flow/bulk-update/",
            method: "PATCH",
            body: data,
          };
        }
        return {
          url: "/api/revenue/auto-invoice-flow/bulk-create/",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "AutomationList", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) => {
        await handleQueryLifecycle(queryFulfilled);
      },
    }),

    getPortfolioByFeePlan: builder.query<any, { id: Id; isUpdate: boolean }>({
      query: ({ isUpdate, id }) => {
        if (isUpdate) {
          return {
            url: `/api/revenue/auto-invoice-flow/?fee_plan=${id}`,
            method: "GET",
          };
        }

        return {
          url: `/api/revenue/setup-fee-plan-flow/get-all-portfolios/?fee_plan=${id}`,
          method: "GET",
        };
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        await handleQueryLifecycle(queryFulfilled);
      },
    }),
  }),
});

export const {
  useGetProformaListQuery,
  useDeleteProformaMutation,
  useProformaConvertToInvoiceMutation,
  useSendProformaMutation,
  useGetInvoiceListQuery,
  useDeleteInvoiceMutation,
  useGetFeeCollectionListQuery,
  useDeleteFeeCollectionMutation,
  useFetchClientInvoicesMutation,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useFetchCollectionInvoicesByIdQuery,
  useGetMandateListQuery,
  useFetchMandateByIdQuery,
  useCreateMandateMutation,
  useCancelMandateMutation,
  useGetAutomationListQuery,
  useDeleteSingleAutomationMutation,
  useCreateSingleAutomationMutation,
  useUpdateSingleAutomationMutation,
  useBulkAutomationMutation,
  useLazyGetPortfolioByFeePlanQuery,
  useBulkDeleteAutomationMutation,
} = proformaApi;
